<template>
  <!-- @click.stop — щоб кліки всередині дропдауна не закривали його через handleDocumentClick -->
  <div class="notification-dropdown" @click.stop>
    <!-- Шапка -->
    <div class="notification-dropdown__header">
      <h3 class="notification-dropdown__title">{{ t('notifications.title') }}</h3>
      <button
        v-if="hasUnread"
        type="button"
        class="notification-dropdown__mark-all"
        @click="handleMarkAllRead"
      >
        {{ t('notifications.markAllRead') }}
      </button>
    </div>

    <!-- Вкладки billing / system / promo — pill-кнопки; одна вкладка без переключателя -->
    <div v-if="visibleTabCount > 1" class="notification-dropdown__tabs">
      <button
        v-if="showBillingTab"
        class="notification-dropdown__tab"
        :class="{ '--active': activeTab === 'billing' }"
        @click="$emit('switchTab', 'billing')"
      >
        {{ t('notifications.tabBilling') }}
        <span v-if="billingUnread > 0" class="tab-badge">{{ billingUnread > 99 ? '99+' : billingUnread }}</span>
      </button>
      <button
        v-if="showSystemTab"
        class="notification-dropdown__tab"
        :class="{ '--active': activeTab === 'system' }"
        @click="$emit('switchTab', 'system')"
      >
        {{ t('notifications.tabSystem') }}
        <span v-if="systemUnread > 0" class="tab-badge">{{ systemUnread > 99 ? '99+' : systemUnread }}</span>
      </button>
      <!-- Пропозиції дилера: только владельцу аккаунта и только если кампании приходили -->
      <button
        v-if="showPromoTab"
        class="notification-dropdown__tab"
        :class="{ '--active': activeTab === 'promo' }"
        @click="$emit('switchTab', 'promo')"
      >
        {{ t('notifications.tabPromo') }}
        <span v-if="promoUnread > 0" class="tab-badge">{{ promoUnread > 99 ? '99+' : promoUnread }}</span>
      </button>
    </div>

    <!-- Фильтр ленты: поиск по номеру, трекеру, геозоне и тип события -->
    <div class="notification-dropdown__filters">
      <div class="search-bar notification-dropdown__search">
        <span class="material-symbols-outlined search-icon">search</span>
        <input
          type="search"
          class="search-input"
          :value="search"
          :placeholder="t('notifications.searchPlaceholder')"
          :aria-label="t('notifications.searchPlaceholder')"
          enterkeyhint="search"
          @input="$emit('update:search', $event.target.value)"
        />
        <button
          v-if="search"
          type="button"
          class="search-clear"
          :aria-label="t('notifications.clearSearch')"
          @click="$emit('update:search', '')"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
      <select
        v-if="typeOptions.length > 1"
        class="notification-dropdown__type"
        :class="{ '--active': filterType }"
        :value="filterType"
        :aria-label="t('notifications.typeFilter')"
        @change="$emit('update:filterType', $event.target.value)"
      >
        <option value="">{{ t('notifications.allTypes') }}</option>
        <option v-for="type in typeOptions" :key="type" :value="type">
          {{ t(`notifications.types.${type}`) }}
        </option>
      </select>
    </div>

    <!-- Контент -->
    <div class="notification-dropdown__content">
      <!-- Пілюля «N нових — оновити» (з'являється коли під час читання прийшли нові) -->
      <button
        v-if="pendingNewCount > 0 && !loading"
        type="button"
        class="notification-dropdown__refresh"
        @click="$emit('refresh')"
      >
        <span class="material-symbols-outlined">arrow_upward</span>
        {{ t('notifications.newPending', { count: pendingNewCount }) }}
      </button>

      <div v-if="loading" class="notification-dropdown__loading">
        <span class="notification-dropdown__spinner"></span>
      </div>
      <!-- При активном фильтре пустой список значит «не нашлось», а не «уведомлений нет» -->
      <div v-else-if="notifications.length === 0 && filtersActive" class="notification-dropdown__empty">
        <span class="material-symbols-outlined">search_off</span>
        <p>{{ t('notifications.emptyFiltered') }}</p>
        <button type="button" class="notification-dropdown__reset" @click="$emit('resetFilters')">
          {{ t('notifications.resetFilters') }}
        </button>
      </div>
      <div v-else-if="notifications.length === 0" class="notification-dropdown__empty">
        <span class="material-symbols-outlined">notifications_off</span>
        <p>{{ activeTab === 'promo' ? t('notifications.emptyPromo') : activeTab === 'system' ? t('notifications.emptySystem') : t('notifications.emptyBilling') }}</p>
      </div>
      <ul v-else class="notification-dropdown__list">
        <li
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-item"
          :class="{
            'notification-item--unread': !notification.isRead,
            [`notification-item--${notification.type}`]: notification.type,
          }"
          @click="handleItemClick(notification)"
        >
          <!-- Іконка в кольоровому колі -->
          <span class="notification-item__icon material-symbols-outlined">
            {{ getIcon(notification.type) }}
          </span>
          <div class="notification-item__content">
            <strong class="notification-item__title">{{ notification.title }}</strong>
            <p class="notification-item__message">{{ getShortMessage(notification.message) }}</p>
            <!-- Госномер рядом со временем: по нему ищут, в тексте уведомления его нет -->
            <div class="notification-item__meta">
              <span class="notification-item__time">{{ formatTime(notification.createdAt) }}</span>
              <span v-if="notification.devicePlate" class="plate-badge">{{ notification.devicePlate }}</span>
            </div>
          </div>
          <span v-if="!notification.isRead" class="notification-item__dot"></span>
        </li>
        <li v-if="hasMore" class="notification-dropdown__load-more-row">
          <button
            type="button"
            class="notification-dropdown__load-more"
            :disabled="loadingMore"
            @click="$emit('loadMore')"
          >
            <span v-if="loadingMore" class="notification-dropdown__spinner notification-dropdown__spinner--sm"></span>
            <span v-else>{{ t('notifications.loadMore') }}</span>
          </button>
        </li>
      </ul>
    </div>

    <!-- Футер — налаштування сповіщень -->
    <div class="notification-dropdown__footer" @click="$emit('goSettings')">
      <span class="notification-dropdown__footer-icon material-symbols-outlined">settings</span>
      <div class="notification-dropdown__footer-text">
        <strong>{{ t('notifications.settingsTitle') }}</strong>
      </div>
      <span class="notification-dropdown__footer-arrow material-symbols-outlined">chevron_right</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { t, getLanguage } from '../services/i18nService'

const props = defineProps({
  notifications: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  loadingMore: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  pendingNewCount: { type: Number, default: 0 },
  activeTab: { type: String, default: 'billing' },
  billingUnread: { type: Number, default: 0 },
  systemUnread: { type: Number, default: 0 },
  showBillingTab: { type: Boolean, default: true },
  showSystemTab: { type: Boolean, default: true },
  promoUnread: { type: Number, default: 0 },
  showPromoTab: { type: Boolean, default: false },
  // Фильтр ленты: строка поиска, выбранный тип и доступные типы вкладки
  search: { type: String, default: '' },
  filterType: { type: String, default: '' },
  typeOptions: { type: Array, default: () => [] },
  filtersActive: { type: Boolean, default: false },
})

const emit = defineEmits([
  'close', 'markRead', 'markAllRead', 'openNotification',
  'switchTab', 'goSettings', 'loadMore', 'refresh',
  'update:search', 'update:filterType', 'resetFilters',
])

const hasUnread = computed(() => props.notifications.some((n) => !n.isRead))

const visibleTabCount = computed(() =>
  [props.showBillingTab, props.showSystemTab, props.showPromoTab].filter(Boolean).length
)

// Strip HTML tags and truncate for preview
const getShortMessage = (message) => {
  if (!message) return ''
  const text = message.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
  if (text.length > 80) {
    return text.substring(0, 80) + '...'
  }
  return text
}

const getIcon = (type) => {
  const icons = {
    // Біллінгові типи
    invoice_issued: 'receipt_long',
    payment_reminder: 'schedule',
    payment_overdue: 'warning',
    warning: 'warning',
    suspension_notice: 'pause_circle',
    block_notice: 'block',
    unblock_notice: 'check_circle',
    custom: 'mail',
    campaign: 'campaign', // пропозиція дилера: картинка, текст, кнопка
    // Системні типи
    geozone_enter: 'login',
    geozone_exit: 'logout',
    tracker_loss: 'signal_wifi_off',
    tracker_online: 'signal_wifi_4_bar',
    sensor_trigger: 'sensors',
    alarm_trigger: 'crisis_alert',
    dtc: 'car_repair', // несправність: червона лампа щитка / Check Engine
    tacho: 'timer', // тахограф: їзда без картки, пороги часу керування
    station_card: 'local_gas_station', // порушення картки АЗС: ліміт, блокування, строк, доступ
    station_intake: 'water_drop', // прихід пального на заправку
    sent_etoll_no_fix: 'gps_off', // машина з SENT/e-TOLL їде по Польщі без координат
  }
  return icons[type] || 'notifications'
}

// Абсолютна дата і час замість відносного "X хв тому"
const formatTime = (dateString) => {
  if (!dateString) return ''
  let date
  if (dateString.endsWith('Z') || dateString.includes('+')) {
    date = new Date(dateString)
  } else {
    date = new Date(dateString.replace(' ', 'T') + 'Z')
  }

  const lang = getLanguage()
  const locale = lang === 'uk' ? 'uk-UA' : lang === 'ru' ? 'ru-RU' : 'en-US'

  return date.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const handleItemClick = (notification) => {
  if (!notification.isRead) {
    emit('markRead', notification.id)
  }
  emit('openNotification', notification)
}

const handleMarkAllRead = () => {
  emit('markAllRead')
}
</script>

<style scoped>
/* ===== Контейнер ===== */
.notification-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 400px;
  /* 580, а не 520: строка фильтра съела бы место у списка */
  max-height: 580px;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===== Шапка ===== */
.notification-dropdown__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.25rem 0.75rem;
}

.notification-dropdown__title {
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-heading);
  margin: 0;
}

.notification-dropdown__mark-all {
  font-size: var(--text-sm);
  color: var(--primary);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  transition: background 0.15s;
}

.notification-dropdown__mark-all:hover {
  background: var(--primary-bg);
}

/* ===== Pill-вкладки ===== */
.notification-dropdown__tabs {
  display: flex;
  gap: 0.5rem;
  padding: 0 1.25rem 1rem;
}

.notification-dropdown__tab {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 1rem;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border: 1px solid transparent;
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: all 0.15s;
}

.notification-dropdown__tab:hover {
  color: var(--text-heading);
  background: var(--bg-tertiary);
}

.notification-dropdown__tab.--active {
  color: var(--primary);
  background: var(--primary-bg);
  border-color: var(--primary-light);
  font-weight: 600;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  color: var(--text-inverse);
  font-size: 0.6875rem;
  font-weight: 700;
  border-radius: var(--radius-full);
  line-height: 1;
  background: var(--primary);
}

.notification-dropdown__tab.--active .tab-badge {
  background: var(--primary);
}

/* ===== Контент ===== */
.notification-dropdown__content {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
  border-top: 1px solid var(--border-light);
}

.notification-dropdown__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.notification-dropdown__spinner {
  width: 28px;
  height: 28px;
  border: 2.5px solid var(--border-default);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.notification-dropdown__spinner--sm {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

/* Пілюля «N нових — оновити» */
.notification-dropdown__refresh {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  width: calc(100% - 1.5rem);
  margin: 0.5rem 0.75rem 0;
  padding: 0.5rem 0.75rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  background: var(--primary-bg);
  border: 1px solid var(--primary-light);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: background 0.15s;
}

.notification-dropdown__refresh:hover {
  background: var(--primary-light);
}

.notification-dropdown__refresh .material-symbols-outlined {
  font-size: 16px;
}

/* «Завантажити ще» */
.notification-dropdown__load-more-row {
  list-style: none;
  display: flex;
  justify-content: center;
  padding: 0.5rem 1.25rem 0.75rem;
}

.notification-dropdown__load-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0.4rem 1rem;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--primary);
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.15s;
}

.notification-dropdown__load-more:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.notification-dropdown__load-more:disabled {
  cursor: default;
  opacity: 0.7;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.notification-dropdown__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  color: var(--text-muted);
}

.notification-dropdown__empty .material-symbols-outlined {
  font-size: 48px;
  margin-bottom: 0.75rem;
  opacity: 0.4;
}

.notification-dropdown__empty p {
  margin: 0;
  font-size: var(--text-sm);
}

/* ===== Список сповіщень ===== */
.notification-dropdown__list {
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 0.875rem 1.25rem;
  cursor: pointer;
  transition: background 0.15s;
  position: relative;
}

.notification-item:hover {
  background: var(--bg-secondary);
}

.notification-item--unread {
  background: var(--primary-bg);
}

.notification-item--unread:hover {
  background: var(--primary-light);
}

/* ===== Іконка в кольоровому колі ===== */
.notification-item__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 20px;
}

/* --- Біллінгові типи --- */
.notification-item--warning .notification-item__icon,
.notification-item--payment_overdue .notification-item__icon {
  background: var(--amber-50);
  color: var(--amber-600);
}

.notification-item--block_notice .notification-item__icon {
  background: var(--danger-light);
  color: var(--danger);
}

.notification-item--suspension_notice .notification-item__icon {
  background: var(--warning-light);
  color: var(--amber-600);
}

.notification-item--unblock_notice .notification-item__icon {
  background: var(--success-light);
  color: var(--success);
}

.notification-item--invoice_issued .notification-item__icon,
.notification-item--payment_reminder .notification-item__icon {
  background: var(--primary-bg);
  color: var(--primary);
}

.notification-item--custom .notification-item__icon {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.notification-item--campaign .notification-item__icon {
  background: var(--primary-bg);
  color: var(--primary);
}

/* --- Системні типи --- */
.notification-item--geozone_enter .notification-item__icon,
.notification-item--geozone_exit .notification-item__icon {
  background: var(--primary-bg);
  color: var(--primary);
}

.notification-item--tracker_loss .notification-item__icon {
  background: var(--danger-light);
  color: var(--danger);
}

.notification-item--tracker_online .notification-item__icon {
  background: var(--success-light);
  color: var(--success);
}

.notification-item--sensor_trigger .notification-item__icon {
  background: var(--amber-50);
  color: var(--amber-600);
}

.notification-item--alarm_trigger .notification-item__icon {
  background: var(--danger-light);
  color: var(--danger);
}

.notification-item--dtc .notification-item__icon {
  background: var(--warning-light);
  color: var(--warning-text);
}

.notification-item--station_intake .notification-item__icon {
  background: var(--success-light);
  color: var(--success-text);
}

.notification-item--station_card .notification-item__icon {
  background: var(--warning-light);
  color: var(--warning-text);
}

.notification-item--tacho .notification-item__icon {
  background: var(--primary-light);
  color: var(--primary-text);
}

/* ===== Текст сповіщення ===== */
.notification-item__content {
  flex: 1;
  min-width: 0;
}

.notification-item__title {
  display: block;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-heading);
  margin-bottom: 0.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-item__message {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 0 0 0.3rem;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-item__time {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.notification-item__dot {
  position: absolute;
  top: 1rem;
  right: 1.25rem;
  width: 8px;
  height: 8px;
  background: var(--primary);
  border-radius: 50%;
}

/* ===== Футер — карточка налаштувань =====
   Акцентна, а не сіра: раніше на --bg-secondary вона зливалася зі списком
   сповіщень і читалась як ще один рядок. Первинний колір + верхня межа
   відділяють її як єдину дію-кнопку в дропдауні. */
.notification-dropdown__footer {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.5rem 0.75rem 0.75rem;
  padding: 0.75rem 0.875rem;
  background: var(--primary-bg);
  border: 1px solid var(--primary-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

/* Тонка лінія над футером — візуально відриває його від стрічки сповіщень */
.notification-dropdown__footer::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -0.5rem;
  height: 1px;
  background: var(--border-light);
}

.notification-dropdown__footer:hover {
  background: var(--primary-light);
  border-color: var(--primary);
}

.notification-dropdown__footer-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 20px;
  background: var(--primary);
  color: var(--bg-card);
}

.notification-dropdown__footer-text {
  flex: 1;
  min-width: 0;
}

.notification-dropdown__footer-text strong {
  display: block;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-heading);
}

.notification-dropdown__footer-text p {
  margin: 0.15rem 0 0;
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: 1.35;
}

.notification-dropdown__footer-arrow {
  flex-shrink: 0;
  font-size: 20px;
  color: var(--text-muted);
}

/* ===== Фильтр ленты: поиск + тип события ===== */
.notification-dropdown__filters {
  display: flex;
  gap: 0.5rem;
  padding: 0 1.25rem 0.75rem;
}

.notification-dropdown__search {
  flex: 1;
}

.notification-dropdown__type {
  flex: 0 1 45%;
  min-width: 0;
  height: 34px;
  padding: 0 0.5rem;
  font-family: inherit;
  font-size: var(--text-sm);
  color: var(--text-primary);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  text-overflow: ellipsis;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.notification-dropdown__type:focus {
  outline: none;
  border-color: var(--primary);
}

/* Выбран конкретный тип: подсвечиваем, чтобы было видно, что лента отфильтрована */
.notification-dropdown__type.--active {
  color: var(--primary);
  background: var(--primary-bg);
  border-color: var(--primary-light);
  font-weight: 600;
}

.notification-dropdown__reset {
  margin-top: 0.75rem;
  padding: 0.35rem 0.75rem;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--primary);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s;
}

.notification-dropdown__reset:hover {
  background: var(--primary-bg);
}

/* Время и госномер в одной строке */
.notification-item__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* ===== Mobile / tablet ===== */
@media (max-width: 768px) {
  .notification-dropdown {
    position: fixed;
    top: 60px;
    left: 8px;
    right: 8px;
    width: auto;
    max-height: calc(100vh - 80px);
  }

  /* 16px — иначе iOS Safari зумит страницу при фокусе на поле */
  .notification-dropdown__search .search-input,
  .notification-dropdown__type {
    font-size: 16px;
  }
}
</style>
